import React, { useEffect, useState, useCallback } from 'react';
import { Service } from './Service';
import { Row, Col, Button, Input, Label, Card, CardHeader, CardBody, Badge, Spinner } from 'reactstrap';
import Select from 'react-select';

const Commands = () => {
    const service = React.useMemo(() => new Service(), []);
    const [commands, setCommands] = useState([]);
    const [command, setCommand] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [recipies, setRecipies] = useState([]);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [cmds, recs] = await Promise.all([
                service.getCommands(),
                service.getRecipies()
            ]);
            setCommands(cmds || []);
            setRecipies(recs || []);
            if (cmds && cmds.length > 0) {
                setCommand(prev => {
                    if (prev && prev.id) {
                        return cmds.find(c => c.id === prev.id) || cmds[0];
                    }
                    return cmds[0];
                });
            }
        } catch (error) {
            console.error('Error loading commands:', error);
        } finally {
            setLoading(false);
        }
    }, [service]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const addCommand = () => {
        const defaultRecipe = recipies.length > 0 ? recipies[0] : null;
        const newCommand = {
            title: `Εντολή #${commands.length + 1}`,
            description: '',
            recipeId: defaultRecipe ? defaultRecipe.id : '',
            recipyId: defaultRecipe ? defaultRecipe.id : '',
            qty: defaultRecipe ? defaultRecipe.qty : 1000,
            status: 'Αναμονή'
        };

        setCommand(newCommand);
    };

    const saveCommand = async () => {
        if (!command) return;
        try {
            setLoading(true);
            if (command.id) {
                const updated = await service.saveCommand(command);
                setCommands(commands.map(c => c.id === command.id ? (updated || command) : c));
                if (updated) setCommand(updated);
            } else {
                const created = await service.addCommand(command);
                setCommands([created, ...commands]);
                setCommand(created);
            }
        } catch (error) {
            console.error('Error saving command:', error);
        } finally {
            setLoading(false);
        }
    };

    const deleteCommand = async (id) => {
        if (!id) {
            setCommand(commands.length > 0 ? commands[0] : null);
            return;
        }
        if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την εντολή παραγωγής;')) return;
        try {
            setLoading(true);
            const success = await service.deleteCommand(id);
            if (success) {
                const nextCmds = commands.filter(c => c.id !== id);
                setCommands(nextCmds);
                setCommand(nextCmds.length > 0 ? nextCmds[0] : null);
            }
        } catch (error) {
            console.error('Error deleting command:', error);
        } finally {
            setLoading(false);
        }
    };

    const selectedRecipe = recipies.find(r => r.id === (command?.recipeId || command?.recipyId));
    const recipeBaseQty = selectedRecipe ? (selectedRecipe.qty || 1) : 1;
    const batchMultiplier = command ? (command.qty ? (command.qty / recipeBaseQty) : 1) : 1;

    const filteredCommands = commands.filter(c => 
        !search || 
        ((c.title && c.title.toLowerCase().includes(search.toLowerCase())) || 
         (c.description && c.description.toLowerCase().includes(search.toLowerCase())))
    );

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Completed':
            case 'Ολοκληρώθηκε':
                return <Badge color="success"><i className="bi bi-check-circle me-1"></i> Ολοκληρώθηκε</Badge>;
            case 'InProgress':
            case 'Σε εξέλιξη':
                return <Badge color="info"><i className="bi bi-arrow-repeat me-1"></i> Σε εξέλιξη</Badge>;
            default:
                return <Badge color="secondary"><i className="bi bi-clock me-1"></i> Αναμονή</Badge>;
        }
    };

    return (
        <Row className="g-4">
            <Col lg="4" md="5">
                <div className="ek-card mb-0">
                    <div className="ek-card-header">
                        <h5>
                            <i className="bi bi-clipboard2-check-fill text-primary"></i> Εντολές Παραγωγής
                        </h5>
                        <Badge color="info" pill>{commands.length}</Badge>
                    </div>
                    <div className="ek-card-body">
                        <Button onClick={addCommand} block color="primary" className="mb-3">
                            <i className="bi bi-plus-lg"></i> Προσθήκη Εντολής
                        </Button>

                        <div className="ek-search-box">
                            <i className="bi bi-search"></i>
                            <Input
                                type="search"
                                className="ek-search-input"
                                placeholder="Αναζήτηση εντολής..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        <div className="scrollable-list">
                            {loading && commands.length === 0 ? (
                                <div className="text-center py-4">
                                    <Spinner size="sm" color="primary" />
                                    <span className="ms-2 text-muted">Φόρτωση...</span>
                                </div>
                            ) : filteredCommands.length === 0 ? (
                                <div className="ek-empty-state py-4">
                                    <i className="bi bi-inbox fs-2"></i>
                                    <p className="small text-muted mb-0">Δεν βρέθηκαν εντολές παραγωγής</p>
                                </div>
                            ) : (
                                <div className="ek-list-group">
                                    {filteredCommands.map(c => {
                                        const r = recipies.find(rc => rc.id === (c.recipeId || c.recipyId));
                                        return (
                                            <div
                                                key={c.id || c.title}
                                                className={`ek-list-item ${command && command.id === c.id ? 'active' : ''}`}
                                                onClick={() => setCommand(c)}
                                            >
                                                <div>
                                                    <div className="ek-list-item-title">{c.title || 'Χωρίς Τίτλο'}</div>
                                                    <div className="ek-list-item-sub">
                                                        <span>{r ? r.title : (c.recipeTitle || 'Χωρίς Συνταγή')}</span>
                                                        <span>•</span>
                                                        <strong>{c.qty || 0} gr</strong>
                                                    </div>
                                                </div>
                                                <div>
                                                    {getStatusBadge(c.status)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Col>

            <Col lg="8" md="7">
                {command ? (
                    <Card className="ek-card">
                        <CardHeader className="ek-card-header">
                            <h5>
                                <i className="bi bi-pencil-square text-primary"></i> 
                                {command.title || 'Επεξεργασία Εντολής'}
                            </h5>
                            <div className="d-flex align-items-center gap-2">
                                {getStatusBadge(command.status)}
                            </div>
                        </CardHeader>
                        <CardBody className="ek-card-body">
                            <Row>
                                <Col md="6">
                                    <div className="ek-form-group mb-3">
                                        <Label className="ek-label">
                                            <i className="bi bi-card-heading"></i> Τίτλος Εντολής
                                        </Label>
                                        <Input
                                            value={command.title || ''}
                                            placeholder="π.χ. Παραγωγή Πρωινής Βάρδιας"
                                            onChange={(e) => setCommand({ ...command, title: e.target.value })}
                                        />
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div className="ek-form-group mb-3">
                                        <Label className="ek-label">
                                            <i className="bi bi-journal-text"></i> Επιλογή Συνταγής
                                        </Label>
                                        <Select
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            value={selectedRecipe ? { 
                                                value: selectedRecipe.id, 
                                                label: `${selectedRecipe.title} (Βάση: ${selectedRecipe.qty} gr)` 
                                            } : null}
                                            onChange={(selectedOption) => {
                                                const recipeId = selectedOption ? selectedOption.value : '';
                                                const r = recipies.find(rc => rc.id === recipeId);
                                                const currentMultiplier = batchMultiplier || 1;
                                                setCommand({ 
                                                    ...command, 
                                                    recipeId: recipeId,
                                                    recipyId: recipeId,
                                                    recipeTitle: r ? r.title : '',
                                                    qty: r ? Math.round(r.qty * currentMultiplier) : command.qty
                                                });
                                            }}
                                            options={recipies.map(r => ({ 
                                                value: r.id, 
                                                label: `${r.title} (Βάση: ${r.qty} gr)` 
                                            }))}
                                            isClearable
                                            placeholder="Επιλέξτε Συνταγή..."
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md="6">
                                    <div className="ek-form-group mb-3">
                                        <Label className="ek-label">
                                            <i className="bi bi-calculator"></i> Αριθμός Δόσεων / Παρτίδων (Multiplier)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            min="0.1"
                                            value={parseFloat(batchMultiplier.toFixed(2)) || 1}
                                            onChange={(e) => {
                                                const multiplier = parseFloat(e.target.value) || 0;
                                                const baseQty = selectedRecipe ? selectedRecipe.qty : 1000;
                                                setCommand({ 
                                                    ...command, 
                                                    qty: Math.round(multiplier * baseQty) 
                                                });
                                            }}
                                        />
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div className="ek-form-group mb-3">
                                        <Label className="ek-label">
                                            <i className="bi bi-speedometer"></i> Συνολική Ποσότητα Παραγωγής (gr)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="any"
                                            value={command.qty || ''}
                                            onChange={(e) => setCommand({ ...command, qty: e.target.valueAsNumber || 0 })}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            {selectedRecipe && (
                                <div className="p-3 mb-3 bg-light rounded-3 border">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="fw-semibold text-dark">
                                            <i className="bi bi-info-circle text-primary me-1"></i> Ανάλυση Παρτίδας:
                                        </span>
                                        <Badge className="badge-brand">
                                            {batchMultiplier.toFixed(2)}x συνταγή = {command.qty} gr
                                        </Badge>
                                    </div>
                                    <div className="small text-muted">
                                        Η συνταγή <strong>{selectedRecipe.title}</strong> περιλαμβάνει {selectedRecipe.ingredients?.length || 0} συστατικά. Οι ποσότητες θα κλιμακωθούν αυτόματα κατά την εκτέλεση.
                                    </div>
                                </div>
                            )}

                            <div className="ek-form-group mb-4">
                                <Label className="ek-label">
                                    <i className="bi bi-card-text"></i> Περιγραφή / Παρατηρήσεις
                                </Label>
                                <Input
                                    type="textarea"
                                    rows="3"
                                    value={command.description || ''}
                                    placeholder="Σημειώσεις για τον χειριστή παραγωγής..."
                                    onChange={(e) => setCommand({ ...command, description: e.target.value })}
                                />
                            </div>

                            <div className="d-flex gap-2 pt-2 border-top">
                                <Button onClick={saveCommand} color="primary" disabled={loading}>
                                    <i className="bi bi-check2-circle"></i> Αποθήκευση Εντολής
                                </Button>
                                {command.id && (
                                    <Button onClick={() => deleteCommand(command.id)} color="danger" disabled={loading}>
                                        <i className="bi bi-trash3"></i> Διαγραφή
                                    </Button>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                ) : (
                    <div className="ek-card">
                        <div className="ek-empty-state">
                            <i className="bi bi-clipboard2-check"></i>
                            <h4>Επιλέξτε μία εντολή παραγωγής</h4>
                            <p>Επιλέξτε μία εντολή από την αριστερή λίστα για προβολή ή πατήστε "Προσθήκη Εντολής" για νέα.</p>
                        </div>
                    </div>
                )}
            </Col>
        </Row>
    );
};

export { Commands };
export default Commands;
